/**
 * SurveyGiftFlow — P_SURVEY_GIFT_1.0 "A gift from us".
 *
 * Self-contained and vertical-neutral: it takes the survey slug, the response
 * id and the gift config, and nothing else. Wording is "respondent / friend /
 * gift" only.
 *
 * One thing per screen, 44px+ targets, works at 390px:
 *   choice   → up to three big buttons (skipped when only one is enabled)
 *   form     → the respondent's own email + one terms tick box
 *   working  → "Generating your gift…" (min 7s) for any friend gift
 *   done     → the designed card, plus the respondent's own share controls
 *
 * The friend's message always leaves from the RESPONDENT'S own email or
 * messages app. The platform never contacts the friend.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Gift, Users, Heart, Mail, MessageSquare, Link2, Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';

const API = (typeof window !== 'undefined' ? window.location.origin : '');
const MIN_GENERATING_MS = 7000;
const MESSAGE_MAX = 300;

const MODE_META = {
    self: { icon: Gift, key: 'getMine' },
    friend: { icon: Users, key: 'giveFriend' },
    both: { icon: Heart, key: 'getGive' },
};

const GiftCardImage = ({ card, testId }) => (
    <div className="rounded-2xl overflow-hidden bg-black/20" data-testid={testId}>
        <img
            src={card.image_url}
            alt={card.title || ''}
            loading="eager"
            className="w-full h-auto block"
            data-testid={`${testId}-img`}
        />
    </div>
);

const SurveyGiftFlow = ({ slug, responseId, config, language, onSkip }) => {
    const { t } = useTranslation();
    const modes = ['self', 'friend', 'both'].filter((m) => (
        m === 'self' ? config.allow_self !== false
            : m === 'friend' ? config.allow_friend !== false
                : config.allow_both !== false
    ));
    const [step, setStep] = useState(modes.length === 1 ? 'form' : 'choice');
    const [mode, setMode] = useState(modes.length === 1 ? modes[0] : null);
    const [email, setEmail] = useState('');
    const [accepted, setAccepted] = useState(false);
    const [showTerms, setShowTerms] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const [note, setNote] = useState('');
    const [copied, setCopied] = useState(false);
    const [shared, setShared] = useState(false);
    const timerRef = useRef(null);

    useEffect(() => () => clearTimeout(timerRef.current), []);

    const friendMode = mode === 'friend' || mode === 'both';
    // The clinic's own offer, shown before the choices so the respondent knows
    // what the gift is.
    const offerText = ((config.respondent_gift || {}).title || '').trim();

    const submit = async () => {
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(email.trim())) {
            setError(t('surveyGift.errors.email', 'Please enter a valid email address.'));
            return;
        }
        if (!accepted) {
            setError(t('surveyGift.errors.terms', 'Please accept the terms to continue.'));
            return;
        }
        setError(null);
        setStep(friendMode ? 'working' : 'sending');
        const startedAt = Date.now();
        try {
            const res = await fetch(`${API}/api/surveys/public/${encodeURIComponent(slug)}/gift/claim`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    response_id: responseId,
                    email: email.trim(),
                    mode,
                    tc_accepted: true,
                    language,
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                const detail = typeof data.detail === 'string' ? data.detail : 'gift_delivery_failed';
                setError(t(`surveyGift.errors.${detail}`, t('surveyGift.errors.generic', 'Something went wrong. Please try again.')));
                setStep('form');
                return;
            }
            const wait = friendMode ? Math.max(0, MIN_GENERATING_MS - (Date.now() - startedAt)) : 0;
            timerRef.current = setTimeout(() => {
                setResult(data);
                setStep('done');
            }, wait);
        } catch {
            setError(t('surveyGift.errors.generic', 'Something went wrong. Please try again.'));
            setStep('form');
        }
    };

    const friendCard = result?.friend;
    const ownCard = result?.respondent;
    const shareBody = () => {
        const intro = note.trim()
            ? `${note.trim().replace(/https?:\/\/\S+/gi, '').slice(0, MESSAGE_MAX)}\n\n`
            : '';
        return `${intro}${t('surveyGift.share.body', 'Here is a gift for you:')} ${friendCard?.public_url || ''}`;
    };
    const shareSubject = () => t('surveyGift.share.subject', 'A gift for you');

    // Real anchors, not scripted navigation: iOS opens Mail / Messages from a
    // tapped mailto:/sms: link far more reliably, and the respondent's own app
    // is always the sender.
    const mailtoHref = `mailto:?subject=${encodeURIComponent(shareSubject())}&body=${encodeURIComponent(shareBody())}`;
    const smsHref = `sms:?&body=${encodeURIComponent(shareBody())}`;
    const copyIt = async () => {
        try {
            await navigator.clipboard.writeText(friendCard?.public_url || '');
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        } catch { /* clipboard unavailable */ }
    };

    const back = () => {
        setError(null);
        if (step === 'form' && modes.length > 1) { setStep('choice'); setMode(null); return; }
        onSkip?.();
    };

    const BackButton = () => (
        <button
            type="button"
            onClick={back}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground min-h-[44px] px-1"
            data-testid="survey-gift-back-btn"
        >
            <ArrowLeft className="w-4 h-4" />
            {t('common.back', 'Back')}
        </button>
    );

    // ── Screen 1 — which gift ─────────────────────────────────────────
    if (step === 'choice') {
        return (
            <div className="space-y-4" data-testid="survey-gift-choice">
                <div className="text-center space-y-1">
                    <p className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {t('surveyGift.title', 'A gift from us')}
                    </p>
                    <p className="text-sm text-muted-foreground">
                        {t('surveyGift.subtitle', 'Thank you for your time. Choose your gift.')}
                    </p>
                    {offerText && (
                        <p className="text-lg font-semibold pt-1" data-testid="survey-gift-offer" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {offerText}
                        </p>
                    )}
                </div>
                <div className="space-y-3">
                    {modes.map((m) => {
                        const Icon = MODE_META[m].icon;
                        return (
                            <Button
                                key={m}
                                variant={m === 'both' ? 'default' : 'outline'}
                                className="relative w-full min-h-[56px] h-auto justify-start gap-3 px-4 py-3 text-base font-semibold whitespace-normal text-left [&_svg]:size-6"
                                onClick={() => { setMode(m); setStep('form'); }}
                                data-testid={`survey-gift-mode-${m}`}
                            >
                                <Icon />
                                <span>{t(`surveyGift.mode.${MODE_META[m].key}`, m)}</span>
                                {m === 'both' && (
                                    <span
                                        className="absolute -top-2 right-3 rounded-full bg-emerald-600 text-white text-[10px] font-bold px-2 py-0.5 shadow"
                                        data-testid="survey-gift-popular-badge"
                                    >
                                        {t('surveyGift.popular', 'Most popular')}
                                    </span>
                                )}
                            </Button>
                        );
                    })}
                </div>
                <div className="text-center">
                    <button
                        type="button"
                        onClick={onSkip}
                        className="text-sm text-muted-foreground underline underline-offset-4 min-h-[44px] px-2"
                        data-testid="survey-gift-skip-btn"
                    >
                        {t('surveyGift.noThanks', 'No thanks')}
                    </button>
                </div>
            </div>
        );
    }

    // ── Screen 2 — email + terms ──────────────────────────────────────
    if (step === 'form') {
        return (
            <div className="space-y-4" data-testid="survey-gift-form">
                <BackButton />
                <p className="text-lg font-bold text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {t('surveyGift.emailHeading', 'Where should we send your gift?')}
                </p>
                <input
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('surveyGift.emailPlaceholder', 'your@email.com')}
                    className="w-full min-h-[52px] rounded-xl border border-slate-300 dark:border-slate-600 bg-transparent px-4 text-base outline-none focus:border-slate-500"
                    data-testid="survey-gift-email-input"
                />
                <label className="flex items-start gap-3 text-left">
                    <input
                        type="checkbox"
                        checked={accepted}
                        onChange={(e) => setAccepted(e.target.checked)}
                        className="mt-1 w-5 h-5"
                        data-testid="survey-gift-terms-checkbox"
                    />
                    <span className="text-xs text-muted-foreground">
                        {t('surveyGift.terms.accept', 'I accept the terms of this gift.')}{' '}
                        <button
                            type="button"
                            className="underline"
                            onClick={() => setShowTerms((v) => !v)}
                            data-testid="survey-gift-terms-link"
                        >
                            {t('surveyGift.terms.viewLink', 'View terms')}
                        </button>
                    </span>
                </label>
                {showTerms && (
                    <p className="text-[11px] leading-relaxed text-muted-foreground max-h-40 overflow-y-auto" data-testid="survey-gift-terms-body">
                        {t('surveyGift.terms.body', 'One gift per person. The gift cannot be used on the day it is issued, cannot be exchanged for cash and is valid only until the date shown on the card.')}
                    </p>
                )}
                {error && (
                    <p className="text-sm text-red-500 text-center" data-testid="survey-gift-error">{error}</p>
                )}
                <Button
                    className="w-full min-h-[56px] text-base font-semibold"
                    onClick={submit}
                    data-testid="survey-gift-submit-btn"
                >
                    {t('surveyGift.continue', 'Continue')}
                </Button>
            </div>
        );
    }

    // ── Generating / sending ──────────────────────────────────────────
    if (step === 'working' || step === 'sending') {
        return (
            <div className="py-10 text-center" data-testid="survey-gift-generating">
                <div
                    className="rounded-2xl px-4 py-6 border border-slate-200 dark:border-white/10 animate-status-pulse motion-reduce:animate-none"
                    role="status"
                    aria-live="polite"
                >
                    <Loader2 className="w-7 h-7 mx-auto mb-3 animate-spin" />
                    <p className="text-base font-bold uppercase tracking-wide">
                        {t('surveyGift.generating', 'Generating your gift…')}
                    </p>
                </div>
            </div>
        );
    }

    // ── Screen 3 — the designed card(s) ───────────────────────────────
    return (
        <div className="space-y-5" data-testid="survey-gift-done">
            {friendCard ? (
                <>
                    <p className="text-lg font-bold text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {t('surveyGift.friendReady', "Your friend's gift is ready")}
                    </p>
                    <GiftCardImage card={friendCard} testId="survey-gift-friend-card" />
                    <div className="space-y-2">
                        <textarea
                            value={note}
                            maxLength={MESSAGE_MAX}
                            onChange={(e) => setNote(e.target.value)}
                            rows={3}
                            placeholder={t('surveyGift.messagePlaceholder', 'Add a short message (optional)')}
                            className="w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-transparent px-3 py-2 text-base outline-none focus:border-slate-500"
                            data-testid="survey-gift-message-input"
                        />
                        <p className="text-[11px] text-muted-foreground text-right">{note.length}/{MESSAGE_MAX}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Button asChild className="w-full min-h-[56px] text-base font-semibold">
                            <a
                                href={mailtoHref}
                                onClick={() => setShared(true)}
                                data-testid="survey-gift-email-it-btn"
                            >
                                <Mail />
                                {t('surveyGift.emailIt', 'Email it')}
                            </a>
                        </Button>
                        <Button asChild className="w-full min-h-[56px] text-base font-semibold">
                            <a
                                href={smsHref}
                                onClick={() => setShared(true)}
                                data-testid="survey-gift-text-it-btn"
                            >
                                <MessageSquare />
                                {t('surveyGift.textIt', 'Text it')}
                            </a>
                        </Button>
                    </div>
                    {shared && (
                        <p className="text-xs text-center text-muted-foreground" data-testid="survey-gift-send-again-hint">
                            {t('surveyGift.sendAgainHint', 'Not sent? Open it again — it is the same gift link.')}
                        </p>
                    )}
                    <button
                        type="button"
                        onClick={copyIt}
                        className="w-full text-sm text-muted-foreground min-h-[44px] inline-flex items-center justify-center gap-1.5"
                        data-testid="survey-gift-copy-link-btn"
                    >
                        <Link2 className="w-3.5 h-3.5" />
                        {copied ? t('surveyGift.copied', 'Link copied') : t('surveyGift.copyLink', 'Copy link')}
                    </button>
                </>
            ) : null}

            {ownCard ? (
                <>
                    <p className="text-lg font-bold text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {friendCard
                            ? t('surveyGift.yoursOnItsWay', 'Your gift is on its way to your email')
                            : t('surveyGift.yoursReady', 'Here is your gift')}
                    </p>
                    {!friendCard && <GiftCardImage card={ownCard} testId="survey-gift-own-card" />}
                    <p className="text-xs text-center text-muted-foreground" data-testid="survey-gift-emailed-note">
                        {t('surveyGift.emailedTo', 'We emailed it to {{email}}.', { email: email.trim() })}
                    </p>
                </>
            ) : null}

            <Button
                variant="outline"
                className="w-full min-h-[56px] text-base font-semibold"
                onClick={onSkip}
                data-testid="survey-gift-done-btn"
            >
                {t('surveyGift.done', 'Done')}
            </Button>
        </div>
    );
};

export default SurveyGiftFlow;
